<template>
  <div class="inventory-wrapper">
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableLocal
      v-else-if="hasPermission('sites.view')
        && hasPermission('sites.products.view')
        && hasPermission('productInventory.view')"
      :isSearchable="false"
      :items="displayedProductInventory"
      :pagination="pagination"
      :disablePagination="true"
      :headers="tableHeaders"
      :itemActions="itemActions"
      :isCustomIconNext="true"
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #item.quantity="{item}">
        <div>
          <VTextField
            v-model="item.quantity"
            solo
            flat
            min="0"
            type="number"
            extraClass="quantityProduct"
            :rules="{ min: 0, max: 5 }"
            persistentHint
          />
        </div>
      </template>
      <template #item.order="{item}">
        <v-btn
          small
          color="primary"
          class="addBtn m-2"
          :disabled="item.quantity <= 0"
          @click.stop="onClickAddToOrder(item)"
        >
          {{ $t('forms.labels.add') }}
        </v-btn>
      </template>
      <template #item.lastMeasure="{item}">
        <template v-if="item.lastMeasure">
          <div
            v-if="item.lastMeasure.quantity"
            class="last-quantity-container"
          >
            <span class="locationCapacity">{{ item.lastMeasure.quantity }}</span>
            <span class="locationCapacity-data">{{ formatLastMeasureDate(item.lastMeasure.createdAt) }}</span>
          </div>
        </template>
      </template>
      <template #item.quantityMin="{item}">
        <span class="center-num-col">{{ item.quantityMin }}</span>
      </template>
      <template #item.container="{item}">
        <span class="center-num-col">{{ item.container }}</span>
      </template>
      <template #item.unit="{item}">
        <span class="center-num-col">{{ item.unit }}</span>
      </template>
      <template #item.locationCapacity="{item}">
        <span class="center-num-col">{{ item.locationCapacity }}</span>
      </template>
    </VTableLocal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Vue from 'vue';
import VTableLocal from '@/components/shared/content-views/VTableLocal';
import InventoryForm from '@/components/forms/InventoryForm';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import roles from '@/constants/roles';

export default {
    components: {
        VTableLocal,
        VTextField,
    },
    props: {
        site: {
            type: Object,
            default: () => {},
        },
        product: {
            type: Object,
            default: () => {},
        },
        refreshInventory: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            products: [],
            pagination: {},
            loading: false,
            queryParams: {
                perpage: 25,
            },
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'hasRole',
        ]),
        tableHeaders() {
            const arr = [
                'location',
                'lastMeasure',
                'unit',
                'quantityMin',
                'locationCapacity',
                'container',
                'quantity',
                'order',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
            return arr;
        },
        actionsArr() {
            const arr = [];
            if (this.hasPermission('productInventory.edit')) {
                arr.push('edit');
            } else if (this.hasPermission('productInventory.remove')) {
                arr.push('close');
            }
            return arr;
        },
        displayedProductInventory() {
            return this.products.map(product => ({
                ...product,
                quantity: 0,
                order: product.id,
                expiration: this.$options.filters.formatDate(product.expiration, 'L'),
            }));
        },
        itemActions() {
            const actions = [];

            if (this.hasPermission('standards.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasRole(roles.ADMINISTRATOR.value)
                || this.hasRole(roles.MANAGER.value)
                || this.hasRole(roles.SALES_MANAGER.value)
                || this.hasRole(roles.SALES_REPRESENTATIVE.value)) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    watch: {
        async lang() {
            await this.getProductInventory();
        },
        product: {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal.id !== oldVal?.id) {
                    this.getProductInventory();
                }
            },
        },
        refreshInventory: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.getProductInventory();
                }
            },
        },
    },
    methods: {
        ...mapActions({
            reloadOrders: 'getOrderItems',
        }),
        async getProductInventory() {
            this.loading = true;
            const { data } = await this.$rq.getProductInventory(this.site.id, this.product.id, this.queryParams);
            this.products = data.inventory;
            this.loading = false;
        },
        async getProductInventoryItem(inventoryId) {
            const { data } = await this.$rq.getProductInventoryItem(this.site.id, this.product.id, inventoryId, { showallattributes: !!this.hasPermission('showAllAttributes') });
            return data;
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams };
            await this.getProductInventory();
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.$root.$emit('showModal', {
                    title: this.$t('inventory.add'),
                    component: Vue.component('InventoryForm', InventoryForm),
                    onConfirm: requestPayload => {
                        this.addInventoryItem(requestPayload);
                    },
                });
            }
            if (action.mode === 'edit') {
                const productInventory = await this.getProductInventoryItem(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('inventory.update'),
                    component: Vue.component('InventoryForm', InventoryForm),
                    payload: {
                        inventory: productInventory,
                        isEdition: true,
                    },
                    onConfirm: requestPayload => {
                        this.updateInventoryItem(item.id, requestPayload);
                    },
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('inventory.remove'),
                    component: Vue.component('confirmRemove', ConfirmForm),
                    payload: {
                        msg: this.$t('inventory.confirmRemove', { locationName: item.location, productName: this.product.name }),
                        removeMsgSecond: this.$t('inventory.removeMsgSecond'),
                    },
                    onConfirm: () => this.removeInventoryItem(item.id),
                    isForm: false,
                });
            }
        },
        async addInventoryItem(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            await this.$rq.addProductInventoryItem(this.site.id, this.product.id, payload);
            this.$toasted.global.toast_success({
                message: this.$t('inventory.added'),
            });
            await this.getProductInventory();
        },
        async updateInventoryItem(inventoryId, requestPayload) {
            await this.$rq.updatedProductInventoryItem(this.site.id, this.product.id, inventoryId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('inventory.updated'),
            });
            await this.getProductInventory();
        },
        async removeInventoryItem(inventoryId) {
            await this.$rq.removeProductInventoryItem(this.site.id, this.product.id, inventoryId);
            this.$toasted.global.toast_success({
                message: this.$t('inventory.removed'),
            });
            await this.getProductInventory();
        },
        formatLastMeasureDate(dt) {
            let dtStr = '';
            if (dt) {
                dtStr = this.$options.filters.formatDate(dt);
            }
            return dtStr;
        },
        async onClickAddToOrder(item) {
            const { data } = await this.$rq.addItemToOrder(this.site.id, { ...item, productName: this.product.name });
            if (data?.id) {
                this.$toasted.global.toast_success({
                    message: this.$t('msg.productOrderAdded', { product: this.product.name, location: data.location }),
                });
                // eslint-disable-next-line no-param-reassign
                item.quantity = 0;
                this.reloadOrders({ siteId: this.site.id });
            }
        },
    },
};
</script>
<style lang="scss">
    @media(min-width: 601px) {
        .center-num-col{
            padding-left: 8px;
        }
        .last-quantity-container {
            padding-left: 8px;
        }
    }
    .last-quantity-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .locationCapacity-data {
        color: #BDC2D0;
    }
    .addBtn {
        height: 42px !important;
    }
    .quantityProduct {
        width: 85px;
        border-radius: 5%;
        border: 1px solid;
        text-align: center;
        height: 42px;

        input {
            text-align: center !important;
        }
        .v-input__slot:before {
            border-style: none !important;
        }
        .v-input__slot {
            background-color: white !important;
            min-height: 40px !important;
        }
    }
</style>
